// Site-wide SEO helpers + structured data
import { CONTACT, LICENCES, GOOGLE_REVIEWS, QVOLT_LOGO } from "./content";
import { currentGoogleReviews } from "./googleReviews";

// Canonical / og:url derivation strategy:
//
// 1. If REACT_APP_PUBLIC_SITE_URL is set (production launch), pin every
//    canonical and og:url to that origin regardless of which host actually
//    serves the request. This is critical during DNS propagation: any
//    visitor who lands on the preview URL still emits a canonical to the
//    production domain, so Google never indexes the preview as a duplicate.
// 2. Otherwise (local dev, no env), fall back to window.location.origin so
//    canonicals follow the current host (preserves existing dev behavior).
const PUBLIC_SITE_URL = (process.env.REACT_APP_PUBLIC_SITE_URL || "").replace(/\/$/, "");

function deriveSiteUrl() {
  if (PUBLIC_SITE_URL) return PUBLIC_SITE_URL;
  if (typeof window !== "undefined" && window.location && window.location.origin) {
    return window.location.origin.replace(/\/$/, "");
  }
  return "";
}

export const SITE_URL = deriveSiteUrl();

export const absUrl = (path = "/") =>
  `${deriveSiteUrl()}${path.startsWith("/") ? path : `/${path}`}`;

// Phase 28: share/JSON-LD image: the 1200×630 card, not the tiny logo.
const defaultOgImage = () => absUrl("/og-default.jpg");

export const SITE_TITLE_TAIL =
  "QVolt Solar, Queensland's family-owned solar installer";

export const PAGE_META = {
  home: {
    title: "Solar panels and batteries in Queensland",
    description:
      "Family-owned QLD solar installer with 30-year workmanship warranty, NETCC approved. Get a free no-obligation quote across Brisbane, Gold Coast and Sunshine Coast.",
    path: "/",
  },
  about: {
    title: "About: Dan and the QVolt team",
    description:
      "Meet Dan and the QVolt crew. 10+ years of solar experience, QLD #88205 and NSW 466291c licensed, NETCC approved. Family-owned, Queensland.",
    path: "/about",
  },
  services: {
    title: "Services: solar panels, batteries, installation",
    description:
      "Tailored solar systems with quality panels, premium batteries and licensed installation across QLD and NSW. 30/30/10/10 year warranties.",
    path: "/services",
  },
  contact: {
    title: "Contact QVolt Solar",
    description:
      "Get a free quote, ask the team a question, or call us. We respond within one business day. Office: 53/28 Burnside Rd, Ormeau QLD.",
    path: "/contact",
  },
  blog: {
    title: "Learning Centre: solar guides and updates",
    description:
      "Honest plain-English guides on solar panels, batteries, system sizing and Australian rebates from the QVolt team.",
    path: "/learning-centre",
  },
  learning_centre: {
    title: "Learning Centre: Solar made simple.",
    description:
      "Honest, plain-English guides and short videos from Dan and the QVolt team. Calculators, articles and how-to videos for Australian solar buyers.",
    path: "/learning-centre",
  },
  calculator: {
    title: "Solar calculator: estimate your payback and ROI",
    description:
      "Free, no-fluff Australian solar calculator. See your estimated payback period, annual savings and 25-year ROI based on your bill, suburb and system size.",
    path: "/solar-calculator",
  },
  // Phase 36: Google Solar API roof + savings checker.
  solar_checker: {
    title: "Solar panel checker: see your roof and savings",
    description:
      "Free solar panel checker for Australian homes. Enter your address to see your roof from above, how many panels fit, the sunshine it gets and what a system would save on your bill. Upload a bill for a tailored result.",
    path: "/solar-panel-checker",
  },
  // Phase 19: dedicated service-detail landing pages hanging off /services.
  // Titles + descriptions live here so the shared ServiceDetail component
  // stays purely presentational. Each page also passes its own JSON-LD
  // Service + BreadcrumbList block via `serviceDetailJsonLd` /
  // `serviceDetailBreadcrumbJsonLd`.
  service_solar_installation: {
    title: "Solar Panel Installation Services in Australia",
    description:
      "Get solar panel installation by local experts in Australia. Cut power bills with quality systems, tailored advice and hassle-free installs.",
    path: "/services/solar-panel-installation",
  },
  service_battery_installation: {
    title: "Battery Storage Installation for Homes and Businesses",
    description:
      "Get expert battery storage installation with QVolt Solar to cut power bills, boost backup protection, and use more of your solar energy.",
    path: "/services/battery-storage-installation",
  },
  service_solar_packages: {
    title: "Solar Panel Sales and Packages in Australia",
    description:
      "Explore solar panel sales and packages in Australia, with tailored system design and installation to cut bills and boost energy independence.",
    path: "/services/solar-panel-sales-and-packages",
  },
};

export const buildTitle = (left) => `${left} | ${SITE_TITLE_TAIL}`;

export function localBusinessJsonLd() {
  const og = defaultOgImage();
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "QVolt Solar",
    image: og,
    url: deriveSiteUrl(),
    telephone: CONTACT.phone || "1300 960 073",
    email: CONTACT.email || "support@qvoltsolar.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "53/28 Burnside Rd",
      addressLocality: "Ormeau",
      addressRegion: "QLD",
      postalCode: "4208",
      addressCountry: "AU",
    },
    areaServed: [
      "Brisbane",
      "Gold Coast",
      "Sunshine Coast",
      "Toowoomba",
      "Coolangatta",
      "North Lakes",
      "Caloundra",
      "Burpengary",
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      // Phase 29: follows the live Places numbers once fetched.
      ratingValue: String(currentGoogleReviews().rating),
      reviewCount: String(currentGoogleReviews().count),
    },
    sameAs: [
      "https://www.facebook.com/Qvoltsolar",
      "https://www.instagram.com/qvoltsolar/",
    ],
    identifier: [
      { "@type": "PropertyValue", name: "QLD electrical licence", value: LICENCES.QLD || "#88205" },
      { "@type": "PropertyValue", name: "NSW electrical licence", value: LICENCES.NSW || "466291c" },
    ],
    logo: og,
  };
}

export function servicesJsonLd() {
  const provider = {
    "@type": "LocalBusiness",
    name: "QVolt Solar",
    url: deriveSiteUrl(),
  };
  const area = ["Brisbane", "Gold Coast", "Sunshine Coast", "Toowoomba"];
  return [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Solar panel sales and packages",
      description:
        "Quality panels from leading solar manufacturers, selected for your roof, your bills and your future plans.",
      provider,
      areaServed: area,
      url: absUrl("/services#solar-panels"),
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Home battery storage",
      description:
        "Cut your reliance on the grid and keep critical circuits running through outages.",
      provider,
      areaServed: area,
      url: absUrl("/services#batteries"),
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Solar panel installation and configuration",
      description:
        "Expert installation and configuration with a 30-year workmanship warranty.",
      provider,
      areaServed: area,
      url: absUrl("/services#installation"),
    },
  ];
}

// Phase 19: per-page Service + BreadcrumbList JSON-LD for the three
// dedicated /services/<slug> detail pages. Each SERVICE_DETAILS entry
// carries the `serviceType` + `shortDescription` that these helpers
// consume, so the copy stays in one place (data/serviceDetails.js).
const SERVICE_DETAIL_AREA_SERVED = [
  "Brisbane",
  "Gold Coast",
  "Sunshine Coast",
  "Toowoomba",
  "Townsville",
];

export function serviceDetailJsonLd(detail) {
  if (!detail || !detail.schema) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: detail.schema.serviceType,
    name: detail.schema.serviceType,
    description:
      detail.schema.shortDescription || detail.seo?.description || "",
    provider: {
      "@type": "LocalBusiness",
      name: "QVolt Solar",
      url: deriveSiteUrl(),
      areaServed: SERVICE_DETAIL_AREA_SERVED,
      identifier: [
        {
          "@type": "PropertyValue",
          name: "QLD electrical licence",
          value: "#88205",
        },
        {
          "@type": "PropertyValue",
          name: "NSW electrical licence",
          value: "466291c",
        },
      ],
    },
    areaServed: SERVICE_DETAIL_AREA_SERVED,
    url: absUrl(`/services/${detail.slug}`),
  };
}

export function serviceDetailBreadcrumbJsonLd(detail) {
  if (!detail) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: deriveSiteUrl() + "/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Services",
        item: absUrl("/services"),
      },
      {
        "@type": "ListItem",
        position: 3,
        name: detail.schema?.serviceType || detail.hero?.eyebrow || detail.slug,
        item: absUrl(`/services/${detail.slug}`),
      },
    ],
  };
}

export function webApplicationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "QVolt Solar Calculator",
    url: absUrl("/solar-calculator"),
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    offers: { "@type": "Offer", price: "0", priceCurrency: "AUD" },
    description:
      "Free Australian solar payback calculator. Estimates payback period, annual savings, lifetime ROI and recommended system size from a postcode and bill.",
    creator: {
      "@type": "Organization",
      name: "QVolt Solar",
      url: deriveSiteUrl(),
    },
  };
}

export function locationServiceJsonLd(loc) {
  if (!loc) return null;
  const licenceId =
    loc.state === "QLD" ? "QLD electrical licence #88205" : "NSW electrical licence #466291c";
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Solar panel and battery installation",
    name: `Solar installers ${loc.name} ${loc.state}`,
    description: loc.hook,
    provider: {
      "@type": "LocalBusiness",
      name: "QVolt Solar",
      url: deriveSiteUrl(),
      identifier: licenceId,
    },
    areaServed: {
      "@type": "City",
      name: loc.name,
      containedInPlace: {
        "@type": "AdministrativeArea",
        name: loc.state_full,
      },
    },
    url: absUrl(`/solar-installers/${loc.slug}`),
  };
}

export function locationBreadcrumbJsonLd(loc) {
  if (!loc) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: deriveSiteUrl() + "/" },
      {
        "@type": "ListItem",
        position: 2,
        name: "Service Areas",
        item: deriveSiteUrl() + "/about#service-areas",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: loc.name,
        item: absUrl(`/solar-installers/${loc.slug}`),
      },
    ],
  };
}

export function videoObjectJsonLd({
  title,
  description,
  thumbnail_url,
  published_at,
  video_id,
  duration_seconds,
}) {
  if (!video_id) return null;
  const block = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: title,
    description: description || "",
    thumbnailUrl: thumbnail_url || `https://i.ytimg.com/vi/${video_id}/hqdefault.jpg`,
    uploadDate: published_at,
    embedUrl: `https://www.youtube-nocookie.com/embed/${video_id}`,
    contentUrl: `https://www.youtube.com/watch?v=${video_id}`,
  };
  if (duration_seconds && duration_seconds > 0) {
    const m = Math.floor(duration_seconds / 60);
    const s = duration_seconds % 60;
    block.duration = `PT${m}M${s}S`;
  }
  return block;
}

export function blogPostingJsonLd({
  title,
  description,
  hero_image_url,
  published_at,
  updated_at,
  author = "Daniel Quinn",
  slug,
}) {
  const og = defaultOgImage();
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description: description || "",
    image: hero_image_url || og,
    datePublished: published_at,
    dateModified: updated_at || published_at,
    author: { "@type": "Person", name: author },
    publisher: {
      "@type": "Organization",
      name: "QVolt Solar",
      logo: { "@type": "ImageObject", url: og },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": absUrl(`/blog/${slug}`) },
  };
}

// Phase 32: suburb landing pages nested under each hub location page.
export function suburbServiceJsonLd(sub) {
  if (!sub) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Solar panel and battery installation",
    name: `Solar installers ${sub.name} QLD ${sub.postcode}`,
    description: sub.tagline,
    provider: {
      "@type": "LocalBusiness",
      name: "QVolt Solar",
      url: deriveSiteUrl(),
      identifier: "QLD electrical licence #88205",
    },
    areaServed: {
      "@type": "Place",
      name: `${sub.name}, Queensland`,
      address: {
        "@type": "PostalAddress",
        addressLocality: sub.name,
        addressRegion: "QLD",
        postalCode: sub.postcode,
        addressCountry: "AU",
      },
      geo: { "@type": "GeoCoordinates", latitude: sub.lat, longitude: sub.lng },
      containedInPlace: { "@type": "City", name: sub.hub_name },
    },
    url: absUrl(sub.path),
  };
}

export function suburbBreadcrumbJsonLd(sub) {
  if (!sub) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: deriveSiteUrl() + "/" },
      { "@type": "ListItem", position: 2, name: "Service Areas", item: deriveSiteUrl() + "/about#service-areas" },
      { "@type": "ListItem", position: 3, name: sub.hub_name, item: absUrl(`/solar-installers/${sub.hub_slug}`) },
      { "@type": "ListItem", position: 4, name: sub.name, item: absUrl(sub.path) },
    ],
  };
}

// Phase 34: solar export checker (Ergon + Energex capacity map).
export function exportCheckerJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "QVolt Solar Export Checker",
    url: absUrl("/solar-export-limits"),
    applicationCategory: "UtilitiesApplication",
    operatingSystem: "Web",
    offers: { "@type": "Offer", price: "0", priceCurrency: "AUD" },
    description:
      "Free checker that tells Queensland homes on the Ergon Energy and Energex networks whether their address can export solar to the grid, using Energy Queensland's public Network Load & Export Capacity Map.",
    creator: { "@type": "Organization", name: "QVolt Solar", url: deriveSiteUrl() },
  };
}

// Phase 39: QVolt Rooftop Report (/rooftop-report) — annual install-trend
// report built from anonymised QVolt installation records.
export function rooftopReportJsonLd({ year, description } = {}) {
  return {
    "@context": "https://schema.org",
    "@type": "Report",
    name: `QVolt Rooftop Report ${year || ""}`.trim(),
    headline: "The year the home battery became the default",
    url: absUrl("/rooftop-report"),
    description:
      description ||
      "How Queensland and northern New South Wales households are installing rooftop solar and batteries, from QVolt's anonymised installation records.",
    inLanguage: "en-AU",
    isAccessibleForFree: true,
    author: { "@type": "Organization", name: "QVolt Solar", url: deriveSiteUrl() },
    publisher: { "@type": "Organization", name: "QVolt Solar", url: deriveSiteUrl() },
    spatialCoverage: ["Queensland, Australia", "New South Wales, Australia"],
  };
}

export function rooftopReportBreadcrumbJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: absUrl("/") },
      { "@type": "ListItem", position: 2, name: "Rooftop Report", item: absUrl("/rooftop-report") },
    ],
  };
}

export function exportAreaJsonLd(area) {
  if (!area) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: `Solar export capacity around ${area.name}, Queensland`,
    description: `Summary of Energy Queensland's published solar export hosting capacity for distribution transformers within ${(area.radius_m / 1000).toFixed(1)} km of ${area.name}.`,
    url: absUrl(area.path),
    isBasedOn: "https://www.ergon.com.au/network/about-us/company-reports,-plans-and-charters/network-load-capacity-map",
    creator: { "@type": "Organization", name: "Energy Queensland" },
    spatialCoverage: {
      "@type": "Place",
      name: `${area.name}, Queensland`,
      geo: { "@type": "GeoCoordinates", latitude: area.lat, longitude: area.lng },
    },
    license: "https://www.ergon.com.au/network/about-us/company-reports,-plans-and-charters/network-load-capacity-map",
  };
}

export function exportAreaBreadcrumbJsonLd(area) {
  if (!area) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: deriveSiteUrl() + "/" },
      { "@type": "ListItem", position: 2, name: "Solar export checker", item: absUrl("/solar-export-limits") },
      { "@type": "ListItem", position: 3, name: area.name, item: absUrl(area.path) },
    ],
  };
}

// Phase 36: Google Solar API roof + savings checker.
export function solarCheckerJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "QVolt Solar Panel Checker",
    url: absUrl("/solar-panel-checker"),
    applicationCategory: "UtilitiesApplication",
    operatingSystem: "Web",
    offers: { "@type": "Offer", price: "0", priceCurrency: "AUD" },
    description:
      "Free tool that shows Australian homeowners their own roof from above using the Google Solar API, how many solar panels fit, the sunshine hours it receives, and the estimated bill savings for any panel count, based on an uploaded electricity bill or typed usage.",
    creator: { "@type": "Organization", name: "QVolt Solar", url: deriveSiteUrl() },
  };
}

export function solarCheckerBreadcrumbJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: deriveSiteUrl() + "/" },
      { "@type": "ListItem", position: 2, name: "Solar calculator", item: absUrl("/solar-calculator") },
      { "@type": "ListItem", position: 3, name: "Solar panel checker", item: absUrl("/solar-panel-checker") },
    ],
  };
}

export function faqJsonLd(faqs) {
  if (!Array.isArray(faqs) || faqs.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function suburbFaqJsonLd(sub) {
  if (!sub || !Array.isArray(sub.faqs) || sub.faqs.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: sub.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}
